import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, ChevronDown, ChevronUp, Trash2, CheckCircle, RefreshCw, X, Loader2 } from 'lucide-react';
import api from '../api/axios';
import { useToast } from './Toast';
import { getPlaceholders } from '../utils/placeholders';
import { filterPhone, isValidEmail } from '../utils/inputValidation';

const STATUTS = [
  { value: '', label: 'Tous' },
  { value: 'actif', label: 'Actifs' },
  { value: 'epuise', label: 'Épuisés' },
  { value: 'expire', label: 'Expirés' },
  { value: 'annule', label: 'Annulés' },
];

const STATUT_STYLE = {
  actif:   { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Actif' },
  epuise:  { bg: 'bg-amber-100',   text: 'text-amber-700',   label: 'Épuisé' },
  expire:  { bg: 'bg-slate-100',   text: 'text-slate-500',   label: 'Expiré' },
  annule:  { bg: 'bg-red-100',     text: 'text-red-600',     label: 'Annulé' },
};

// Modes de paiement selon la devise du salon (identique à la caisse) :
// XOF : Espèces, Wave, Orange Money. Autres devises : Espèces, Carte.
function payMethods(devise) {
  return devise === 'XOF'
    ? ['Especes', 'Wave', 'Orange Money']
    : ['Especes', 'Carte'];
}
const PAY_LABEL = { Especes: 'Espèces', Wave: 'Wave', 'Orange Money': 'Orange Money', Carte: 'Carte' };

function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

function fmtMoney(n, devise) {
  const labels = { XOF: 'FCFA', EUR: '€', USD: '$' };
  const sym = labels[devise] || devise || '';
  return `${Number(n || 0).toLocaleString('fr-FR')} ${sym}`;
}

const norm = (s) => (s || '').toString().toLowerCase();

export default function AdminSubscriptions({ salonDevise, employees = [] }) {
  const showToast = useToast();
  const [subs, setSubs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [statut, setStatut]     = useState('');
  const [expanded, setExpanded] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [useModal, setUseModal] = useState(null); // sub en cours d'utilisation
  const ph = getPlaceholders(salonDevise);

  // On charge tous les abonnements une fois, puis on filtre/recherche côté client :
  // liste courte par salon, pas d'appel API à chaque caractère tapé.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/subscriptions');
      setSubs(res.data.data || []);
    } catch { showToast('Erreur chargement', 'error'); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet abonnement ?')) return;
    try {
      await api.delete(`/subscriptions/${id}`);
      showToast('Abonnement supprimé');
      load();
    } catch { showToast('Erreur', 'error'); }
  };

  const handleUpdateStatut = async (id, nextStatut) => {
    try {
      await api.patch(`/subscriptions/${id}`, { statut: nextStatut });
      showToast('Statut mis à jour');
      load();
    } catch { showToast('Erreur', 'error'); }
  };

  // Stats globales (jamais dépendantes du filtre affiché)
  const actifs  = subs.filter(s => s.statut === 'actif').length;
  const epuises = subs.filter(s => s.statut === 'epuise').length;
  const expires = subs.filter(s => s.statut === 'expire').length;

  // Filtrage + recherche côté client
  const visibles = subs.filter(sub => {
    if (statut && sub.statut !== statut) return false;
    if (search) {
      const q = norm(search);
      if (!norm(sub.clientNom).includes(q) && !norm(sub.label).includes(q) && !norm(sub.clientEmail).includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="flex flex-col h-full bg-slate-50 overflow-auto">

      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 sm:px-6 pt-6 pb-0">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Abonnements</h1>
            <p className="text-sm text-slate-400 mt-0.5">Passages prépayés et forfaits clients</p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-bold shadow hover:bg-indigo-700 transition-colors"
          >
            <Plus size={15} /> Nouvel abonnement
          </button>
        </div>

        {/* Stats rapides (globales) */}
        <div className="flex gap-3 mb-4">
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-50 border border-emerald-100">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            <span className="text-xs font-bold text-emerald-700">{actifs} actif{actifs > 1 ? 's' : ''}</span>
          </div>
          {epuises > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-50 border border-amber-100">
              <span className="w-2 h-2 rounded-full bg-amber-500" />
              <span className="text-xs font-bold text-amber-700">{epuises} épuisé{epuises > 1 ? 's' : ''}</span>
            </div>
          )}
          {expires > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-100 border border-slate-200">
              <span className="w-2 h-2 rounded-full bg-slate-400" />
              <span className="text-xs font-bold text-slate-500">{expires} expiré{expires > 1 ? 's' : ''}</span>
            </div>
          )}
        </div>

        {/* Filtres */}
        <div className="flex flex-col sm:flex-row gap-3 pb-4">
          <div className="relative flex-1">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="search" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher un client ou un forfait…"
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-indigo-400 bg-white"
            />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {STATUTS.map(s => (
              <button key={s.value} onClick={() => setStatut(s.value)}
                className={`px-3 py-2 rounded-xl text-xs font-bold border transition-colors ${statut === s.value ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-500 border-slate-200 hover:border-indigo-300'}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Liste */}
      <div className="flex-1 px-4 sm:px-6 py-5 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-slate-300" />
          </div>
        ) : visibles.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-center">
            <p className="text-sm font-semibold text-slate-400">Aucun abonnement trouvé</p>
            <button onClick={() => setShowForm(true)} className="text-sm font-bold text-indigo-600 underline underline-offset-2">
              Créer le premier
            </button>
          </div>
        ) : visibles.map(sub => {
          const st = STATUT_STYLE[sub.statut] || STATUT_STYLE.actif;
          const pct = sub.totalPassages ? Math.round(((sub.totalPassages - sub.passagesRestants) / sub.totalPassages) * 100) : 0;
          const isOpen = expanded === sub._id;
          return (
            <div key={sub._id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
              {/* Ligne principale */}
              <button
                onClick={() => setExpanded(isOpen ? null : sub._id)}
                className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50 transition-colors"
              >
                {/* Barre de progression */}
                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0 relative overflow-hidden">
                  <div className="absolute bottom-0 left-0 right-0 bg-indigo-500 transition-all" style={{ height: `${pct}%`, opacity: 0.15 }} />
                  <span className="text-xs font-black text-indigo-700 relative z-10">{sub.passagesRestants}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-slate-800 truncate">{sub.clientNom}</p>
                  <p className="text-xs text-slate-400 truncate">{sub.label}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${st.bg} ${st.text}`}>{st.label}</span>
                  <span className="text-xs text-slate-400">{sub.passagesRestants}/{sub.totalPassages}</span>
                  {isOpen ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
                </div>
              </button>

              {/* Détail déployé */}
              {isOpen && (
                <div className="px-4 pb-4 border-t border-slate-100 pt-4 space-y-4">
                  {/* Infos financières */}
                  {(() => {
                    const restedu = Math.max(0, (sub.montantTotal || 0) - (sub.montantPaye || 0));
                    return (
                      <div className="grid grid-cols-3 gap-2 p-3 rounded-xl bg-slate-50 border border-slate-100">
                        <div className="text-center">
                          <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Total forfait</p>
                          <p className="text-sm font-black text-slate-800">{fmtMoney(sub.montantTotal, salonDevise)}</p>
                        </div>
                        <div className="text-center border-x border-slate-200">
                          <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Déjà payé</p>
                          <p className="text-sm font-black text-emerald-600">{fmtMoney(sub.montantPaye, salonDevise)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Reste dû</p>
                          <p className={`text-sm font-black ${restedu > 0 ? 'text-red-500' : 'text-emerald-600'}`}>
                            {restedu > 0 ? fmtMoney(restedu, salonDevise) : 'Soldé'}
                          </p>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Infos client */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <Info label="Passages restants" value={`${sub.passagesRestants} / ${sub.totalPassages}`} />
                    {sub.clientEmail && <Info label="Email" value={sub.clientEmail} />}
                    {sub.clientTel && <Info label="Téléphone" value={sub.clientTel} />}
                    {sub.dateExpiration && <Info label="Expire le" value={fmtDate(sub.dateExpiration)} />}
                    {sub.notes && <Info label="Notes" value={sub.notes} />}
                  </div>

                  {/* Historique des passages */}
                  {sub.usages?.length > 0 && (
                    <div>
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Historique des passages</p>
                      <div className="space-y-1.5 max-h-40 overflow-y-auto">
                        {[...sub.usages].reverse().map(u => (
                          <div key={u._id} className="flex items-center justify-between text-xs py-1.5 px-2 rounded-lg bg-slate-50">
                            <span className="text-slate-500 flex-shrink-0">{fmtDate(u.date)}</span>
                            <span className="text-slate-700 font-medium truncate mx-2">{u.note || 'Passage'}</span>
                            <span className={`flex-shrink-0 font-bold ${u.montant > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                              {u.montant > 0 ? fmtMoney(u.montant, salonDevise) : 'Gratuit'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 pt-1">
                    {sub.statut === 'actif' && sub.passagesRestants > 0 && (
                      <button onClick={() => setUseModal(sub)}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 transition-colors">
                        <CheckCircle size={13} /> Encaisser un passage
                      </button>
                    )}
                    {(sub.statut === 'epuise' || sub.statut === 'expire') && (
                      <button onClick={() => handleUpdateStatut(sub._id, 'annule')}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-100 text-slate-600 text-xs font-bold hover:bg-slate-200 transition-colors">
                        <RefreshCw size={13} /> Marquer annulé
                      </button>
                    )}
                    {sub.statut === 'expire' && sub.passagesRestants > 0 && (
                      <button onClick={() => handleUpdateStatut(sub._id, 'actif')}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-indigo-50 text-indigo-700 text-xs font-bold hover:bg-indigo-100 transition-colors">
                        <RefreshCw size={13} /> Réactiver
                      </button>
                    )}
                    <button onClick={() => handleDelete(sub._id)}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-red-50 text-red-600 text-xs font-bold hover:bg-red-100 transition-colors">
                      <Trash2 size={13} /> Supprimer
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Modale : nouvel abonnement */}
      {showForm && <CreateSubModal onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} salonDevise={salonDevise} ph={ph} />}

      {/* Modale : encaisser un passage */}
      {useModal && <UsePassageModal sub={useModal} salonDevise={salonDevise} employees={employees} onClose={() => setUseModal(null)} onDone={() => { setUseModal(null); load(); }} />}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{label}</p>
      <p className="text-sm font-semibold text-slate-700 mt-0.5 break-words">{value}</p>
    </div>
  );
}

// Sélecteur compact de mode de paiement
function PayMethods({ devise, value, onChange }) {
  return (
    <div className="flex gap-1.5 flex-wrap">
      {payMethods(devise).map(m => (
        <button key={m} type="button" onClick={() => onChange(m)}
          className={`px-3 py-2 rounded-xl text-xs font-bold border transition-colors ${value === m ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-500 border-slate-200 hover:border-indigo-300'}`}>
          {PAY_LABEL[m] || m}
        </button>
      ))}
    </div>
  );
}

function CreateSubModal({ onClose, onCreated, salonDevise, ph }) {
  const showToast = useToast();
  const [saving, setSaving] = useState(false);
  const [methode, setMethode] = useState('Especes');
  const [form, setForm] = useState({
    clientNom: '', clientEmail: '', clientTel: '',
    label: '', totalPassages: '', montantTotal: '', montantPaye: '',
    dateExpiration: '', notes: '',
  });
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }));
  const setTel = e => setForm(p => ({ ...p, clientTel: filterPhone(e.target.value) }));

  const [clients, setClients] = useState([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get('/clients').then(r => { if (alive) setClients(r.data.data || []); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const q = query.trim().toLowerCase();
  const matches = q
    ? clients.filter(c => [c.prenom, c.nom, `${c.prenom || ''} ${c.nom || ''}`, c.telephone]
        .some(v => (v || '').toLowerCase().includes(q))).slice(0, 6)
    : [];

  const pickClient = (c) => {
    setForm(p => ({
      ...p,
      clientNom: `${c.prenom || ''} ${c.nom || ''}`.trim(),
      clientEmail: c.email || '',
      clientTel: filterPhone(c.telephone || ''),
    }));
    setPicked(c._id);
    setQuery('');
    setOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.clientNom || !form.label || !form.totalPassages || form.montantTotal === '') {
      showToast('Remplissez tous les champs requis.', 'error'); return;
    }
    if (form.clientEmail && !isValidEmail(form.clientEmail)) {
      showToast('Adresse email invalide.', 'error'); return;
    }
    const total = Number(form.montantTotal);
    const paye  = Number(form.montantPaye || 0);
    if (paye > total) {
      showToast("L'acompte ne peut pas dépasser le montant total.", 'error'); return;
    }
    if (form.dateExpiration) {
      const exp = new Date(form.dateExpiration);
      exp.setHours(23, 59, 59, 999);
      if (exp < new Date()) {
        showToast("La date d'expiration ne peut pas être dans le passé.", 'error'); return;
      }
    }
    setSaving(true);
    try {
      await api.post('/subscriptions', { ...form, methodePaiement: methode });
      showToast('Abonnement créé');
      onCreated();
    } catch (err) {
      showToast(err.response?.data?.message || 'Erreur', 'error');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h2 className="text-base font-black text-slate-800">Nouvel abonnement</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors">
            <X size={14} className="text-slate-600" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {clients.length > 0 && (
            <div className="relative">
              <div className="relative">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={query}
                  onChange={e => { setQuery(e.target.value); setOpen(true); }}
                  onFocus={() => setOpen(true)}
                  onBlur={() => setTimeout(() => setOpen(false), 150)}
                  placeholder="Importer un client existant (nom ou téléphone)"
                  aria-label="Rechercher un client existant"
                  className="w-full pl-8 pr-3 py-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 text-xs outline-none focus:border-indigo-400 focus:bg-white"
                />
              </div>
              {open && q && (
                <ul className="absolute z-10 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden max-h-48 overflow-y-auto">
                  {matches.length === 0 ? (
                    <li className="px-3 py-2 text-xs text-slate-400">Aucun client trouvé</li>
                  ) : matches.map(c => (
                    <li key={c._id}>
                      <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => pickClient(c)}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-indigo-50 flex items-center justify-between gap-2">
                        <span className="font-bold text-slate-700 truncate">{`${c.prenom || ''} ${c.nom || ''}`.trim() || c.telephone || 'Client sans nom'}</span>
                        <span className="text-slate-400 shrink-0">{(`${c.prenom || ''} ${c.nom || ''}`.trim() && c.telephone) || ''}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {picked && (
                <p className="mt-1 text-[11px] text-emerald-600">Client importé. Vous pouvez modifier les champs ci-dessous.</p>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <FInput label="Client *" value={form.clientNom} onChange={set('clientNom')} placeholder={ph.nomComplet} required />
            <FInput label="Téléphone" value={form.clientTel} onChange={setTel} placeholder={ph.telephone} type="tel" />
          </div>
          <FInput label="Email" value={form.clientEmail} onChange={set('clientEmail')} placeholder={ph.email} type="email" />
          <FInput label="Nom du forfait *" value={form.label} onChange={set('label')} placeholder="Ex : Forfait 10 coupes" required />
          <div className="grid grid-cols-2 gap-3">
            <FInput label="Nb de passages *" value={form.totalPassages} onChange={set('totalPassages')} type="number" min="1" placeholder="10" required />
            <FInput label={`Montant total (${salonDevise || 'FCFA'}) *`} value={form.montantTotal} onChange={set('montantTotal')} type="number" min="0" placeholder="0" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FInput label={`Acompte versé (${salonDevise || 'FCFA'})`} value={form.montantPaye} onChange={set('montantPaye')} type="number" min="0" placeholder="0" />
            <FInput label="Date d'expiration" value={form.dateExpiration} onChange={set('dateExpiration')} type="date" min={new Date().toISOString().split('T')[0]} />
          </div>
          {Number(form.montantPaye || 0) > 0 && (
            <div>
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Mode de paiement de l'acompte</label>
              <PayMethods devise={salonDevise} value={methode} onChange={setMethode} />
            </div>
          )}
          <div>
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Notes</label>
            <textarea value={form.notes} onChange={set('notes')} placeholder="Notes internes…"
              className="w-full p-3 rounded-xl border border-slate-200 text-sm outline-none focus:border-indigo-400 resize-none h-16" />
          </div>
          <button type="submit" disabled={saving}
            className="w-full py-3.5 rounded-xl bg-indigo-600 text-white text-sm font-black flex items-center justify-center gap-2 hover:bg-indigo-700 transition-colors disabled:opacity-50">
            {saving ? <><Loader2 size={14} className="animate-spin" /> Enregistrement…</> : 'Créer l\'abonnement'}
          </button>
        </form>
      </div>
    </div>
  );
}

function UsePassageModal({ sub, onClose, onDone, salonDevise, employees = [] }) {
  const showToast = useToast();
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [methode, setMethode] = useState('Especes');

  const restedu = Math.max(0, (sub.montantTotal || 0) - (sub.montantPaye || 0));
  // Suggestion : part par passage restant (arrondie), plutôt que tout le solde d'un coup
  const suggestion = restedu > 0 ? Math.round(restedu / Math.max(1, sub.passagesRestants)) : 0;
  const [montant, setMontant] = useState(String(suggestion));

  const montantNum = Number(montant || 0);
  const depasse = montantNum > restedu;
  const invalide = montant !== '' && (isNaN(montantNum) || montantNum < 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (invalide) {
      showToast('Montant invalide.', 'error'); return;
    }
    if (depasse) {
      showToast(`Le montant ne peut pas dépasser le solde restant (${fmtMoney(restedu, salonDevise)}).`, 'error');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/subscriptions/${sub._id}/use`, { note, montant: montantNum, employeeId: employeeId || null, methodePaiement: methode });
      const restePasses = sub.passagesRestants - 1;
      showToast(`Passage validé. Reste : ${restePasses} passage${restePasses > 1 ? 's' : ''}`);
      onDone();
    } catch (err) {
      showToast(err.response?.data?.message || 'Erreur', 'error');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white w-full sm:max-w-sm sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div>
            <h2 className="text-sm font-black text-slate-800">Valider un passage</h2>
            <p className="text-xs text-slate-400 mt-0.5">{sub.clientNom} · {sub.label}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center">
            <X size={14} className="text-slate-600" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">

          {/* Passages */}
          <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-200">
            <span className="text-xs font-bold text-slate-500">Passages restants après ce passage</span>
            <span className="text-xl font-black text-slate-800">{sub.passagesRestants - 1}</span>
          </div>

          {/* Solde financier */}
          <div className={`grid grid-cols-2 gap-2 p-3 rounded-xl border ${restedu > 0 ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
            <div>
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Forfait total</p>
              <p className="text-sm font-black text-slate-700">{fmtMoney(sub.montantTotal, salonDevise)}</p>
            </div>
            <div>
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Déjà encaissé</p>
              <p className="text-sm font-black text-slate-700">{fmtMoney(sub.montantPaye, salonDevise)}</p>
            </div>
            <div className="col-span-2 border-t border-slate-200 pt-2 mt-1">
              <p className="text-[9px] font-black uppercase tracking-widest mb-0.5 text-slate-400">Reste dû</p>
              <p className={`text-base font-black ${restedu > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                {restedu > 0 ? fmtMoney(restedu, salonDevise) : 'Forfait soldé'}
              </p>
            </div>
          </div>

          {/* Montant à encaisser ce passage */}
          <div>
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">
              {restedu > 0 ? 'Montant encaissé ce passage' : 'Passage gratuit (forfait soldé)'}
            </label>
            <input
              type="number" min="0" max={restedu} value={montant}
              onChange={e => setMontant(e.target.value)}
              disabled={restedu === 0}
              className={`w-full p-3.5 rounded-xl outline-none font-black text-2xl text-center transition-colors
                ${depasse || invalide ? 'bg-red-900 text-white' : 'bg-slate-900 text-white'}
                ${restedu === 0 ? 'opacity-40 cursor-not-allowed' : ''}`}
            />
            {restedu === 0 && <p className="text-xs text-emerald-600 font-bold mt-1 text-center">Ce forfait est entièrement payé, passage gratuit.</p>}
            {depasse && (
              <p className="text-xs text-red-500 font-bold mt-1 text-center">
                Dépasse le solde dû ({fmtMoney(restedu, salonDevise)})
              </p>
            )}
            {montantNum === 0 && restedu > 0 && !invalide && (
              <p className="text-xs text-slate-400 mt-1 text-center">Aucun paiement encaissé pour ce passage.</p>
            )}
          </div>

          {/* Mode de paiement (si encaissement) */}
          {montantNum > 0 && (
            <div>
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Mode de paiement</label>
              <PayMethods devise={salonDevise} value={methode} onChange={setMethode} />
            </div>
          )}

          {/* Employé */}
          {employees.length > 0 && (
            <div>
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Employé (optionnel)</label>
              <select value={employeeId} onChange={e => setEmployeeId(e.target.value)}
                className="w-full p-3 rounded-xl border border-slate-200 text-sm outline-none focus:border-emerald-400 bg-white">
                <option value="">Aucun</option>
                {employees.map(emp => (
                  <option key={emp._id} value={emp._id}>{emp.prenom} {emp.nom}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Note (optionnel)</label>
            <input type="text" value={note} onChange={e => setNote(e.target.value)} placeholder="Ex : Coupe + coiffage"
              className="w-full p-3 rounded-xl border border-slate-200 text-sm outline-none focus:border-emerald-400" />
          </div>

          <button type="submit" disabled={saving || depasse || invalide}
            className="w-full py-3.5 rounded-xl bg-emerald-600 text-white text-sm font-black flex items-center justify-center gap-2 hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {saving ? <><Loader2 size={14} className="animate-spin" /> Enregistrement…</> : <><CheckCircle size={14} /> Valider le passage</>}
          </button>
        </form>
      </div>
    </div>
  );
}

function FInput({ label, ...props }) {
  return (
    <div>
      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">{label}</label>
      <input {...props} className="w-full p-3 rounded-xl border border-slate-200 text-sm outline-none focus:border-indigo-400 bg-white transition-colors" />
    </div>
  );
}
